import React, { useCallback, useMemo, useState } from "react";
import { Button } from "reactstrap";
import { useDropzone } from "react-dropzone";
import { useAuth0 } from "@dhn/components/contexts/react-auth0-spa";
import moment from "moment";
import {
  ALLOWED_RESULT_MIMETYPES,
  appendValidResultFiles,
  getFilesTotalBytes,
  normalizeText,
} from "./result-files";

const getToday = () => moment().format("YYYY-MM-DD");

const createEmptyResultForm = () => ({
  resultDate: getToday(),
  resultLabel: "",
  files: [],
});

const getResultFileIdentity = (fileItem) => {
  if (fileItem?.id) return `id:${fileItem.id}`;
  if (fileItem?.fileKey) return `key:${fileItem.fileKey}`;
  if (fileItem?.localId) return `local:${fileItem.localId}`;
  if (fileItem?.file) return fileItem.file;
  return `${fileItem?.originalName || ""}:${fileItem?.sizeBytes || fileItem?.size || 0}:${fileItem?.mimetype || ""}`;
};

const haveSameResultFiles = (currentFiles = [], baselineFiles = []) =>
  currentFiles.length === baselineFiles.length &&
  currentFiles.every((fileItem, index) =>
    getResultFileIdentity(fileItem) === getResultFileIdentity(baselineFiles[index]));

export default function useMedicalResultsActions({
  options,
  records: sourceRecords,
  onChange,
  appointmentId,
  patientId,
  isPageModalHistory,
  subTabKey,
  recordsKey,
  examIdField,
  examField,
  examHeader,
  resultName,
  endpointName,
  otherResultRecords = [],
}) {
  const { getTokenSilently } = useAuth0();
  const records = useMemo(() => sourceRecords || [], [sourceRecords]);
  const canManageResults = !isPageModalHistory;
  const [selectedExam, setSelectedExam] = useState(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false);
  const [resultDate, setResultDate] = useState(getToday);
  const [resultLabel, setResultLabel] = useState("");
  const [files, setFiles] = useState([]);
  const [resultFormBaseline, setResultFormBaseline] = useState(createEmptyResultForm);
  const [editingRecord, setEditingRecord] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [viewerState, setViewerState] = useState({
    isOpen: false,
    title: "",
    files: [],
    index: 0,
    loading: false,
    error: "",
    record: null,
  });

  const isResultRecord = useCallback((item) =>
    (Array.isArray(item?.files) && item.files.length > 0) ||
    Boolean(normalizeText(item?.resultDate)) ||
    Boolean(normalizeText(item?.examLabel)), []);

  const examRecords = useMemo(() => records.filter((item) => {
    const hasExam = Boolean(Number(item?.[examIdField] || item?.[examField]?.id || 0));
    return hasExam && !isResultRecord(item);
  }), [examField, examIdField, isResultRecord, records]);

  const resultRecords = useMemo(
    () => records.filter((item) => isResultRecord(item)),
    [isResultRecord, records],
  );

  const storedAppointmentBytes = useMemo(
    () => getFilesTotalBytes(
      [...records, ...(otherResultRecords || [])]
        .flatMap((item) => item?.files || []),
    ),
    [otherResultRecords, records],
  );
  const editingRecordBytes = getFilesTotalBytes(editingRecord?.files || []);
  const appointmentBytesOutsideResult = Math.max(
    0,
    storedAppointmentBytes - editingRecordBytes,
  );
  const appointmentUsedBytes =
    appointmentBytesOutsideResult + getFilesTotalBytes(files);
  const hasUnsavedResultChanges = useMemo(() =>
    normalizeText(resultDate) !== normalizeText(resultFormBaseline.resultDate) ||
    normalizeText(resultLabel) !== normalizeText(resultFormBaseline.resultLabel) ||
    !haveSameResultFiles(files, resultFormBaseline.files),
  [files, resultDate, resultFormBaseline, resultLabel]);

  const availableExamOptions = useMemo(() => {
    const selectedIds = new Set(examRecords
      .map((item) => Number(item?.[examIdField] || item?.[examField]?.id || 0))
      .filter(Boolean));
    return (options || []).filter((item) => !selectedIds.has(Number(item?.id || 0)));
  }, [examField, examIdField, examRecords, options]);

  const updateRecords = useCallback((nextRecords) => {
    onChange("tabPrescription", nextRecords, subTabKey, recordsKey);
  }, [onChange, recordsKey, subTabKey]);

  const clearResultForm = useCallback(() => {
    const emptyResultForm = createEmptyResultForm();
    setResultDate(emptyResultForm.resultDate);
    setResultLabel(emptyResultForm.resultLabel);
    setFiles(emptyResultForm.files);
    setResultFormBaseline(emptyResultForm);
    setEditingRecord(null);
    setErrorMessage("");
  }, []);

  const toggleResultsModal = useCallback(() => {
    if (!isResultsModalOpen) {
      setIsResultsModalOpen(true);
      setErrorMessage("");
      return;
    }
    if (hasUnsavedResultChanges) {
      setIsDiscardConfirmationOpen(true);
      return;
    }
    setIsResultsModalOpen(false);
    clearResultForm();
  }, [clearResultForm, hasUnsavedResultChanges, isResultsModalOpen]);

  const handleContinueEditing = useCallback(() => {
    setIsDiscardConfirmationOpen(false);
  }, []);

  const handleDiscardAndClose = useCallback(() => {
    setIsDiscardConfirmationOpen(false);
    setIsResultsModalOpen(false);
    clearResultForm();
  }, [clearResultForm]);

  const handleOpenResultsModal = useCallback(() => {
    setIsResultsModalOpen(true);
    setErrorMessage("");
  }, []);

  const handleAddExam = useCallback((item) => {
    if (isPageModalHistory || !item?.id) return;
    updateRecords([...records, {
      appointmentId,
      patientId,
      [examIdField]: item.id,
      [examField]: item,
    }]);
    setSelectedExam(null);
    setErrorMessage("");
  }, [appointmentId, examField, examIdField, isPageModalHistory, patientId, records, updateRecords]);

  const handleRemoveRecord = useCallback((record) => {
    updateRecords(records.filter((item) => item !== record));
  }, [records, updateRecords]);

  const onDrop = useCallback((acceptedFiles) => {
    if (!canManageResults) return;
    const result = appendValidResultFiles(
      files,
      acceptedFiles,
      appointmentBytesOutsideResult,
    );
    setFiles(result.files);
    setErrorMessage(result.validationError);
  }, [appointmentBytesOutsideResult, canManageResults, files]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    disabled: !canManageResults,
    accept: ALLOWED_RESULT_MIMETYPES.join(", "),
  });

  const handleRemoveLocalFile = useCallback((localId) => {
    setFiles((current) => current.filter((item) => item.localId !== localId));
    setErrorMessage("");
  }, []);

  const handleAddResult = useCallback(() => {
    if (!canManageResults) return;
    if (!normalizeText(resultDate)) {
      setErrorMessage("Debe seleccionar la fecha del resultado.");
      return;
    }
    if (!files.length) {
      setErrorMessage("Debe adjuntar al menos un archivo.");
      return;
    }
    if (!normalizeText(resultLabel)) {
      setErrorMessage("Debe indicar una etiqueta para el resultado.");
      return;
    }

    const payload = {
      appointmentId,
      patientId,
      resultDate,
      examLabel: normalizeText(resultLabel),
      files,
    };
    updateRecords(editingRecord
      ? records.map((item) => item === editingRecord ? { ...item, ...payload } : item)
      : [...records, payload]);
    clearResultForm();
  }, [appointmentId, canManageResults, clearResultForm, editingRecord, files, patientId, records, resultDate, resultLabel, updateRecords]);

  const fetchRemoteBlob = useCallback(async (fileItem) => {
    const accessToken = await getTokenSilently();
    const uri = `${process.env.REACT_APP_APOLLO_CLIENT_URI}/claim-files/${endpointName}`;
    const response = await fetch(uri, {
      body: JSON.stringify({ name: fileItem?.fileKey }),
      method: "POST",
      headers: { "Content-Type": "application/json", authorization: `Bearer ${accessToken}` },
    });
    if (response.status < 200 || response.status > 201) {
      throw new Error("No fue posible obtener el archivo.");
    }
    const contentType = response.headers.get("content-type") || fileItem?.mimetype || "application/octet-stream";
    return new Blob([await response.arrayBuffer()], { type: contentType });
  }, [endpointName, getTokenSilently]);

  const closeViewer = useCallback(() => {
    setViewerState((current) => {
      current.files.forEach((item) => {
        if (item?.previewUrl?.startsWith?.("blob:")) URL.revokeObjectURL(item.previewUrl);
      });
      return { isOpen: false, title: "", files: [], index: 0, loading: false, error: "", record: null };
    });
  }, []);

  const handleOpenViewer = useCallback(async (record) => {
    const filesToShow = Array.isArray(record?.files) ? record.files : [];
    if (!filesToShow.length) {
      setViewerState({ isOpen: true, title: "", files: [], index: 0, loading: false, error: "No hay archivos disponibles.", record });
      return;
    }
    setViewerState({
      isOpen: true,
      title: normalizeText(record?.examLabel) || normalizeText(record?.[examField]?.name) || resultName,
      files: [], index: 0, loading: true, error: "", record,
    });
    try {
      const previewFiles = [];
      for (const fileItem of filesToShow) {
        const blob = fileItem?.file ? fileItem.file : await fetchRemoteBlob(fileItem);
        previewFiles.push({ ...fileItem, mimetype: fileItem?.mimetype || blob.type, previewUrl: URL.createObjectURL(blob) });
      }
      setViewerState((current) => ({ ...current, files: previewFiles, index: 0 }));
    } catch (error) {
      setViewerState((current) => ({ ...current, error: error.message || "No fue posible cargar archivos.", files: [] }));
    } finally {
      setViewerState((current) => ({ ...current, loading: false }));
    }
  }, [examField, fetchRemoteBlob, resultName]);

  const handleDeleteCurrent = useCallback(() => {
    if (!canManageResults) return;
    const currentFile = viewerState.files[viewerState.index];
    const currentRecord = viewerState.record;
    if (!currentFile || !currentRecord || viewerState.files.length <= 1) return;
    const sameFile = (item) =>
      (currentFile?.id && item?.id && Number(currentFile.id) === Number(item.id)) ||
      (currentFile?.localId && item?.localId && currentFile.localId === item.localId) ||
      (currentFile?.fileKey && item?.fileKey && currentFile.fileKey === item.fileKey);
    let updatedRecord = currentRecord;
    const nextRecords = records.map((item) => {
      if (item !== currentRecord) return item;
      updatedRecord = { ...item, files: (item.files || []).filter((file) => !sameFile(file)) };
      return updatedRecord;
    });
    updateRecords(nextRecords);
    const nextFiles = viewerState.files.filter((_, index) => index !== viewerState.index);
    setViewerState((current) => ({ ...current, record: updatedRecord, files: nextFiles, index: Math.max(0, Math.min(current.index, nextFiles.length - 1)) }));
  }, [canManageResults, records, updateRecords, viewerState.files, viewerState.index, viewerState.record]);

  const handleEditResult = useCallback((record) => {
    if (!canManageResults) return;
    const nextResultDate = record?.resultDate || getToday();
    const nextResultLabel = normalizeText(record?.examLabel);
    const nextFiles = (record?.files || []).map((item, index) => ({
      ...item,
      localId: item?.localId || `existing-${item?.id || item?.fileKey || index}`,
    }));
    setEditingRecord(record);
    setResultDate(nextResultDate);
    setResultLabel(nextResultLabel);
    setFiles(nextFiles);
    setResultFormBaseline({
      resultDate: nextResultDate,
      resultLabel: nextResultLabel,
      files: nextFiles,
    });
    setErrorMessage("");
  }, [canManageResults]);

  const columnsExam = useMemo(() => [
    { Header: examHeader, Cell: ({ original }) => original?.[examField]?.name || "N/D" },
    {
      Header: "Opciones", width: 200, show: canManageResults,
      Cell: ({ original }) => canManageResults ? (
        <Button color="danger" type="button" className="btn-color-danger card-header-action" onClick={() => handleRemoveRecord(original)}>
          <i className="fa fa-trash" />{" Borrar"}
        </Button>
      ) : null,
    },
  ], [canManageResults, examField, examHeader, handleRemoveRecord]);

  const columnsResults = useMemo(() => [
    { Header: "Fecha Resultado", className: "text-center", Cell: ({ original }) => original?.resultDate ? moment(original.resultDate).format("DD/MM/YYYY") : "N/D" },
    { Header: "Etiqueta", Cell: ({ original }) => normalizeText(original?.examLabel) || "N/D" },
    { Header: "Archivos", className: "text-center", width: 100, Cell: ({ original }) => (original?.files || []).length },
    {
      Header: "Opciones", width: 360,
      Cell: ({ original }) => {
        const hasFiles = Array.isArray(original?.files) && original.files.length > 0;
        return (
          <div className="d-flex flex-wrap align-items-center">
            <Button color="secondary" type="button" className="btn-color-gray card-header-action mr-2 mb-1" disabled={!hasFiles} onClick={() => handleOpenViewer(original)}>
              <i className="fa fa-eye" />{" Ver archivos"}
            </Button>
            {canManageResults ? <>
              <Button color="secondary" type="button" className="btn-color-gray card-header-action mr-2 mb-1" onClick={() => handleEditResult(original)}>
                <i className="fa fa-pencil" />{" Editar"}
              </Button>
              <Button color="danger" type="button" className="btn-color-danger card-header-action mr-2 mb-1" onClick={() => handleRemoveRecord(original)}>
                <i className="fa fa-trash" />{" Borrar"}
              </Button>
            </> : null}
          </div>
        );
      },
    },
  ], [canManageResults, handleEditResult, handleOpenViewer, handleRemoveRecord]);

  return [{
    selectedExam, availableExamOptions, canManageResults, examRecords, resultRecords,
    columnsExam, columnsResults, isResultsModalOpen, resultDate, resultLabel,
    files, editingRecord, errorMessage, viewerState, getRootProps, getInputProps,
    appointmentUsedBytes, isDiscardConfirmationOpen,
  }, {
    handleAddExam, toggleResultsModal, handleOpenResultsModal,
    handleChangeResultDate: setResultDate,
    handleChangeResultLabel: setResultLabel,
    handleContinueEditing, handleDiscardAndClose,
    handleRemoveLocalFile, handleAddResult, closeViewer,
    handlePreviousFile: () => setViewerState((current) => ({ ...current, index: current.files.length ? (current.index - 1 + current.files.length) % current.files.length : 0 })),
    handleNextFile: () => setViewerState((current) => ({ ...current, index: current.files.length ? (current.index + 1) % current.files.length : 0 })),
    handleDeleteCurrent,
  }];
}
